import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import { fetchWithTenant } from '../utils/apiClient';
import configs from '../config';

const API = configs.BACKEND_URL;

export default function ExtensionAuthPage() {
  const { isAuthenticated } = useAuthStore();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking | authorizing | done | error
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isAuthenticated) {
      // Redirect to login with return URL
      const returnPath = encodeURIComponent('/extension-auth');
      navigate(`/login?redirect=${returnPath}`, { replace: true });
      return;
    }

    // User is logged in — generate bootstrap token
    setStatus('authorizing');

    fetchWithTenant(`${API}/api/user/extension-bootstrap`, { method: 'POST' })
      .then(async (res) => {
        if (!res.ok) throw new Error('Failed to generate authorization token');
        const data = await res.json();
        if (!data.bootstrapToken) throw new Error('No token received');

        // Put token in URL hash so the extension can read it
        window.location.hash = `token=${data.bootstrapToken}`;
        setStatus('done');
      })
      .catch((err) => {
        setError(err.message);
        setStatus('error');
      });
  }, [isAuthenticated, navigate]);

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.logo}>P</div>

        {status === 'checking' && (
          <>
            <h1 style={styles.title}>Checking session...</h1>
            <p style={styles.text}>Verifying your Propel login.</p>
          </>
        )}

        {status === 'authorizing' && (
          <>
            <h1 style={styles.title}>Authorizing extension...</h1>
            <p style={styles.text}>Generating a secure one-time token for the Propel extension.</p>
          </>
        )}

        {status === 'done' && (
          <>
            <h1 style={styles.title}>Extension authorized</h1>
            <p style={styles.text}>You can close this tab and return to Upwork. The Propel extension is now connected.</p>
          </>
        )}

        {status === 'error' && (
          <>
            <h1 style={styles.title}>Authorization failed</h1>
            <p style={{ ...styles.text, color: '#dc2626' }}>{error}</p>
            <button onClick={() => window.location.reload()} style={styles.button}>
              Try again
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f6f7fb',
    fontFamily: 'Inter, -apple-system, sans-serif',
  },
  card: {
    textAlign: 'center',
    maxWidth: '400px',
    padding: '40px',
    background: '#fff',
    borderRadius: '16px',
    boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
  },
  logo: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    background: '#4F46E5',
    color: '#fff',
    fontWeight: 700,
    fontSize: '22px',
    marginBottom: '20px',
  },
  title: {
    margin: '0 0 8px',
    fontSize: '20px',
    fontWeight: 700,
    color: '#1f2937',
  },
  text: {
    margin: 0,
    fontSize: '14px',
    color: '#6b7280',
    lineHeight: 1.5,
  },
  button: {
    marginTop: '16px',
    padding: '10px 24px',
    background: '#4F46E5',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
  },
};
